import React, { ReactNode } from 'react'
import {
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import { RADIUS } from '@/shared/styles/radii'

// chip-shape NotionTextField と同形 Select. label は floating ではなく上 mount.
// raw <Select>+<FormControl>+<MenuItem> の散らかりを集約.
export interface NotionSelectOption<T extends string | number> {
  value: T
  label: ReactNode
  disabled?: boolean
}

interface NotionSelectProps<T extends string | number> {
  label?: ReactNode
  value: T
  onChange: (value: T) => void
  options: NotionSelectOption<T>[]
  placeholder?: string
  disabled?: boolean
  fullWidth?: boolean
  minWidth?: number | string
}

export const NotionSelect = <T extends string | number>({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled,
  fullWidth,
  minWidth = 160,
}: NotionSelectProps<T>) => {
  const handleChange = (event: SelectChangeEvent<T>) => {
    onChange(event.target.value as T)
  }

  return (
    <Stack gap={0.5} sx={{ minWidth, width: fullWidth ? '100%' : undefined }}>
      {label && (
        <Typography component="label" sx={PORTAL_TEXT.caption}>
          {label}
        </Typography>
      )}
      <Select<T>
        value={value}
        onChange={handleChange}
        disabled={disabled}
        displayEmpty
        size="small"
        renderValue={(selected) => {
          const found = options.find((opt) => opt.value === selected)
          if (found) return found.label
          if (placeholder) {
            return (
              <Box component="span" sx={{ color: colorTokens.fg.muted }}>
                {placeholder}
              </Box>
            )
          }
          return null
        }}
        sx={{
          bgcolor: colorTokens.bg.default,
          borderRadius: `${RADIUS.md}px`,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: colorTokens.border.subtle,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: colorTokens.border.default,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: colorTokens.accent.primary,
            borderWidth: 1,
          },
          '& .MuiSelect-select': {
            py: 1,
            px: 1.5,
            fontSize: 14,
          },
        }}
      >
        {options.map((opt) => (
          <MenuItem
            key={String(opt.value)}
            value={opt.value}
            disabled={opt.disabled}
            sx={{ fontSize: 14 }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  )
}
